import React, { useState, useLayoutEffect } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { CustomTextFieldProp } from '@/components/common/CustomTextField';
import PageTitle from '@/components/common/PageTitle';
import HookFormControls, { HookFormControlsProps } from '@/components/common/HookFormControls';
import CompanyType from '@/types/Company.type';
import CompanyService from '@/services/Company.service';
import { FormProvider, useForm } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import { Messages, ContPlan } from '@/common/constants';
import Loading from '@/components/common/Loading';
import PrefectureService from '@/services/Prefecture.service';
import { Prefecture } from '@/types/Prefecture.type';


const Company: React.FC = () => {

  const [prefecture, setPrefecture] = useState<Prefecture[] | []>([]);

  const inputs: Array<HookFormControlsProps> = [
    {
      name: 'name',
      type: 'text',
      label: '会社名',
      rule: {
        required: '会社名は必須項目です。',
        maxLength: {
          value: 255,
          message: '会社名は255字以内で入力してください。',
        },
      },
    },
    {
      name: 'zipCode',
      type: 'text',
      label: '郵便番号(ハイフンなし)',
      rule: {
        required: '郵便番号は必須項目です。',
        pattern: {
          value: new RegExp('^([0-9]{7})$'),
          message: '郵便番号の形式が不正です。',
        },
      },
    },
    {
      name: 'prefecture',
      type: 'autocomplete',
      label: '都道府県',
      options: prefecture,
      getOptionLabel: 'name',
      rule: {
        required: '都道府県は必須項目です。'
      },
    },
    {
      name: 'address1',
      type: 'text',
      label: '住所1',
      rule: {
        required: '住所1は必須項目です。',
        maxLength: {
          value: 255,
          message: '住所1は255字以内で入力してください。',
        },
      },
    },
    {
      name: 'address2',
      type: 'text',
      label: '住所2',
      rule: {
        maxLength: {
          value: 255,
          message: '住所2は255字以内で入力してください。',
        },
      },
    },
    {
      name: 'phoneNumber',
      type: 'text',
      label: '電話番号',
      rule: {
        required: '電話番号は必須項目です。',
        maxLength: {
          value: 15,
          message: '電話番号は15字以内で入力してください。',
        },
      },
    },
    {
      name: 'startDate',
      type: 'date',
      label: '利用開始日',
      disabled: true,
    },
    {
      name: 'endDate',
      type: 'date',
      label: '利用終了日',
      disabled: true,
    },
    {
      name: 'contPlan',
      type: 'select',
      label: '契約プラン',
      options: ContPlan,
      disabled: true,
    },
    {
      name: 'maxUsers',
      type: 'number',
      label: '利用人数',
      helper: '利用人数を増減（10人単位）する場合は、システム管理者にご連絡ください。管理者メールアドレス：mitelink@mitani-corp.co.jp',
      rule: {
        required: '利用人数は必須項目です。',
        // max: { value: 9999, message: '利用人数は9,999以内で入力してください。' },
        // min: { value: 30, message: '利用人数は10以上で入力してください。' },
      },
      disabled: true,
    },
  ];

  const useFormMethods = useForm<CompanyType & Prefecture>({ shouldUnregister: false });
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useFormMethods;
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);


  useLayoutEffect(() => {
    (async () => {
      await PrefectureService.getAll().then((response: { data: Prefecture[] }) => {
        setPrefecture(response.data);
      });
      await CompanyService.get().then((response: { data: CompanyType & Prefecture }) => {
        reset(response.data);
        setLoading(false);
      });
    })();
  }, []);

  const onSubmit = async (values: any) => {
    try {
      const requestValues = { ...values, ...{ prefectures_cd: values.prefecture ? values.prefecture.cd : null } };
      // await CompanyService.update(values);
      await CompanyService.update(requestValues);
      enqueueSnackbar(Messages.update.success, { variant: 'info' });
    } catch (error: any) {
      enqueueSnackbar(error.response.data.message);
      return;
    }
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={12} lg={4}>
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <PageTitle>会社マスタ</PageTitle>
        </Box>
        <Paper variant="outlined" sx={{ my: { xs: 0, md: 0 }, p: { xs: 2, md: 3 } }}>
          <FormProvider {...useFormMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                {inputs.map((input, index) => (
                  <Grid item key={index} xs={12} md={12}>
                    {/* <HookFormControls key={index} {...input} autoComplete="off" /> */}
                    <HookFormControls key={index} {...input} />
                  </Grid>
                ))}
                <Grid item xs={12} md={12}>
                  <Grid container justifyContent="flex-end">
                    <Stack direction="row" spacing={2}>
                      <LoadingButton variant="contained" type="submit" color="primary" loading={isSubmitting}>
                        登録
                      </LoadingButton>
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Paper>
      </Grid>
    </Grid>
  );
};
export default Company;
